import { useState, useEffect, useCallback, useRef } from 'react';
import TabList from '../Shared/TabList';
import TabListTabContent from '../Shared/TabList/components/TabListTabContent';
import TabListNavItem from '../Shared/TabList/components/TabListNavItem';
import SearchTabPanel from './components/SearchTabPanel';
import type { IFindSearchResults } from './models/find-search-results.model';
import SearchAutocomplete, { type ISearchAutocompleteState } from '../Shared/SearchAutocomplete';
import TabHelper from '../Shared/TabList/tab.helper';
import { useTexts } from '../../hooks/texts.hook';
import { IServerSideRendered } from '../Shared/Base/server-side-rendered';

enum SearchPageTabType {
  unified = 'unified',
  faq = 'faq',
}

export interface ISearchPageTexts {
  noUnifiedResultsContent: string;
  emptySuggestion: string;
  noResultRelatedHeading: string;
}

export type ISearchPageSettings = object;

type ISearchPageProps = IServerSideRendered<ISearchPageTexts, ISearchPageSettings>;

function SearchPage({ texts }: ISearchPageProps) {
  const { t } = useTexts();

  const [autocompleteState, setAutompleteState] = useState<ISearchAutocompleteState>({ value: { searchText: '', selectedItem: undefined, preventSearch: true } });
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTabType, setSelectedTabType] = useState(SearchPageTabType.unified);
  const [numberOfUnifiedResults, setNumberOfUnifiedResults] = useState(-1);
  const [numberOfFaqResults, setNumberOfFaqResults] = useState(-1);
  // Defer focus until the API has supplied real counts, then move it only once.
  const shouldFocusSearchResults = useRef(false);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const initialSearchQuery = searchParams.get('q') ?? '';
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSearchQuery(initialSearchQuery);
    setAutompleteState({ value: { searchText: initialSearchQuery, selectedItem: undefined, preventSearch: true } });
    const tabType = searchParams.get('t') === 'faq' ? SearchPageTabType.faq : SearchPageTabType.unified;
    setSelectedTabType(tabType);
    shouldFocusSearchResults.current = initialSearchQuery.trim().length > 0;
  }, []);

  useEffect(() => {
    if (!shouldFocusSearchResults.current || numberOfUnifiedResults < 0 || numberOfFaqResults < 0) return;

    shouldFocusSearchResults.current = false;
    const activeTabIndex = selectedTabType === SearchPageTabType.unified ? 0 : 1;
    const activeTab = document.getElementById(TabHelper.getTabListNavId('search-page', activeTabIndex));
    activeTab?.focus();
  }, [numberOfFaqResults, numberOfUnifiedResults, selectedTabType]);

  function updateSearchParams(tabType: SearchPageTabType | undefined = undefined, query: string | undefined = undefined) {
    const searchParams = new URLSearchParams(window.location.search);
    if (tabType) searchParams.set('t', tabType);
    if (query) searchParams.set('q', query);
    const newUrl = `${window.location.origin}${window.location.pathname}?${searchParams.toString()}`;
    window.history.pushState({ path: newUrl }, '', newUrl);
  }

  function handleOnSubmit() {
    const newSearchQuery = autocompleteState.value.searchText;
    // Reset the result counts and focus flag if the search query has changed.
    if (newSearchQuery !== searchQuery) {
      setNumberOfUnifiedResults(-1);
      setNumberOfFaqResults(-1);
      shouldFocusSearchResults.current = newSearchQuery.trim().length > 0;
    }
    updateSearchParams(undefined, newSearchQuery);
    setSearchQuery(newSearchQuery);
  }

  function handleOnUnifiedActivated() {
    if (selectedTabType === SearchPageTabType.unified) return;
    setSelectedTabType(SearchPageTabType.unified);
    updateSearchParams(SearchPageTabType.unified, undefined);
  }

  function handleOnFaqActivated() {
    if (selectedTabType === SearchPageTabType.faq) return;
    setSelectedTabType(SearchPageTabType.faq);
    updateSearchParams(SearchPageTabType.faq, undefined);
  }

  function handleOnSearchTextChange(newSearchText: string) {
    setAutompleteState({ value: { searchText: newSearchText, selectedItem: undefined, preventSearch: false } });
  }

  function handleTabListSelectedChange(index: number) {
    if (index === 0) handleOnUnifiedActivated();
    else if (index === 1) handleOnFaqActivated();
  }

  const handleOnSearchCompleted = useCallback((numberOfUnified: number, numberOfFaq: number) => {
    setNumberOfUnifiedResults(numberOfUnified);
    setNumberOfFaqResults(numberOfFaq);
  }, []);

  const handleOnSearchQueryChanged = useCallback(
    (newSearchQuery: string) => {
      // Reset the result counts and focus flag if the search query has changed.
      if (newSearchQuery !== searchQuery) {
        setNumberOfUnifiedResults(-1);
        setNumberOfFaqResults(-1);
        shouldFocusSearchResults.current = newSearchQuery.trim().length > 0;
      }
      setSearchQuery(newSearchQuery);
      updateSearchParams(undefined, newSearchQuery);
    },
    [searchQuery],
  );

  return (
    <div>
      <form role="search" className="mb-4" onSubmit={(e) => e.preventDefault()}>
        <div className="d-flex">
          <SearchAutocomplete
            id="search-page-autocomplete"
            labelText={t.search}
            placeholderText={t.search}
            className="w-100"
            state={autocompleteState}
            onSelect={(item) => {
              if (item) {
                if (item.query !== searchQuery) {
                  setNumberOfUnifiedResults(-1);
                  setNumberOfFaqResults(-1);
                  shouldFocusSearchResults.current = true;
                }
                setSearchQuery(item.query);
                updateSearchParams(undefined, item.query);
                setAutompleteState({ value: { searchText: item.query, selectedItem: item, preventSearch: false } });
              }
            }}
            onSearchTextChange={handleOnSearchTextChange}
          />
          <button type="submit" className="btn btn-primary py-0 ms-2" onClick={handleOnSubmit}>
            {t.search}
          </button>
        </div>
      </form>

      <TabList
        id="search-page"
        activeIndex={selectedTabType === SearchPageTabType.unified ? 0 : 1}
        onSelectedChange={handleTabListSelectedChange}
        navItems={[
          <TabListNavItem key={'nav-item-0'} onActivate={handleOnUnifiedActivated}>
            {t('Sidor', 'Pages')} {numberOfUnifiedResults > -1 && <span className="badge text-bg-light ms-1">{numberOfUnifiedResults}</span>}
          </TabListNavItem>,
          <TabListNavItem key={'nav-item-1'} onActivate={handleOnFaqActivated}>
            {t('Frågor och svar', 'Common questions')} {numberOfFaqResults > -1 && <span className="badge text-bg-light ms-1">{numberOfFaqResults}</span>}
          </TabListNavItem>,
        ]}
        tabContents={[
          <TabListTabContent key={'tab-item-0'}>
            <>
              <h2 className="visually-hidden">{t('Sökresultat för sidor', 'Search results for pages')}</h2>
              <SearchTabPanel
                isActive={selectedTabType === SearchPageTabType.unified}
                query={searchQuery}
                searchEndpoint="unified"
                onSearchCompleted={handleOnSearchCompleted}
                onSearchQueryChanged={handleOnSearchQueryChanged}
                numberOfResultsFunc={useCallback((data: IFindSearchResults) => data.numberOfUnifiedResults, [])}
                texts={texts}
              />
            </>
          </TabListTabContent>,
          <TabListTabContent key={'tab-item-1'}>
            <>
              <h2 className="visually-hidden">{t('Sökresultat för frågor och svar', 'Search results for common questions')}</h2>
              <SearchTabPanel
                isActive={selectedTabType === SearchPageTabType.faq}
                query={searchQuery}
                searchEndpoint="faq"
                onSearchCompleted={handleOnSearchCompleted}
                onSearchQueryChanged={handleOnSearchQueryChanged}
                numberOfResultsFunc={useCallback((data: IFindSearchResults) => data.numberOfFaqResults, [])}
                texts={texts}
              />
            </>
          </TabListTabContent>,
        ]}
      ></TabList>
    </div>
  );
}

export default SearchPage;

